import axios from 'axios';
import type {
  ApiResponse,
  PaginatedResponse,
  OfferWithRestaurant,
  Restaurant,
  Offer,
  LoginResponse,
  OfferQueryParams,
  CreateOfferInput,
  CreateRestaurantInput,
} from '../types';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8787/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add JWT token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// ============ PUBLIC APIs ============

export const offersAPI = {
  getAll: (params?: OfferQueryParams) =>
    apiClient.get<PaginatedResponse<OfferWithRestaurant>>('/offers', { params }),

  getById: (id: number) => apiClient.get<ApiResponse<OfferWithRestaurant>>(`/offers/${id}`),

  getFeatured: () => apiClient.get<ApiResponse<OfferWithRestaurant[]>>('/offers/featured'),

  getTrending: () => apiClient.get<ApiResponse<OfferWithRestaurant[]>>('/offers/trending'),

  trackView: (id: number) => apiClient.post<ApiResponse>(`/offers/${id}/view`),
};

export const restaurantsAPI = {
  getAll: () => apiClient.get<ApiResponse<Restaurant[]>>('/restaurants'),

  getById: (id: number) => apiClient.get<ApiResponse<Restaurant>>(`/restaurants/${id}`),

  getOffers: (id: number) => apiClient.get<ApiResponse<Offer[]>>(`/restaurants/${id}/offers`),
};

// ============ AUTH APIs ============

export const authAPI = {
  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>('/auth/login', { email, password }),

  register: (data: { email: string; password: string; name: string }) =>
    apiClient.post<ApiResponse>('/auth/register', data),
};

// ============ ADMIN APIs ============

export const adminAPI = {
  // Offers
  createOffer: (data: CreateOfferInput) => apiClient.post<ApiResponse<Offer>>('/admin/offers', data),

  updateOffer: (id: number, data: Partial<CreateOfferInput>) =>
    apiClient.put<ApiResponse<Offer>>(`/admin/offers/${id}`, data),

  deleteOffer: (id: number) => apiClient.delete<ApiResponse>(`/admin/offers/${id}`),

  toggleOfferStatus: (id: number) => apiClient.patch<ApiResponse<Offer>>(`/admin/offers/${id}/status`),

  toggleOfferFeatured: (id: number) =>
    apiClient.patch<ApiResponse<Offer>>(`/admin/offers/${id}/feature`),

  // Restaurants
  createRestaurant: (data: CreateRestaurantInput) =>
    apiClient.post<ApiResponse<Restaurant>>('/admin/restaurants', data),

  updateRestaurant: (id: number, data: Partial<CreateRestaurantInput>) =>
    apiClient.put<ApiResponse<Restaurant>>(`/admin/restaurants/${id}`, data),

  deleteRestaurant: (id: number) => apiClient.delete<ApiResponse>(`/admin/restaurants/${id}`),

  // Analytics
  getOverview: () =>
    apiClient.get<
      ApiResponse<{
        totalOffers: number;
        activeOffers: number;
        totalRestaurants: number;
        premiumRestaurants: number;
      }>
    >('/admin/analytics/overview'),

  getOfferAnalytics: (id: number) =>
    apiClient.get<ApiResponse<{ views: number; saves: number; redemptions: number }>>(
      `/admin/analytics/offers/${id}`
    ),

  // Upload
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.post<ApiResponse<{ url: string }>>('/admin/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
